import { Router, watchBusy } from "@frui.ts/screens/dist";
import { Modules } from "config";
import type AccountingDocumentsInventoryDetailResponse from "entities/accountingDocumentsInventoryDetailResponse";
import type { interfaces } from "inversify";
import AccountingDocumentsInventoryRepository from "repositories/accountingDocumentsInventoryRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import DetailViewModel from "viewModels/detailViewModel";
import { action, computed } from "mobx";
import AccountingDocumentsInventoryStatus from "entities/accountingDocumentsInventoryStatus";
import AccountingDocumentsInventoryCheckResult from "entities/accountingDocumentsInventoryCheckResult";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "../../../../utils/parseFileNameFromAxiosResponse";
import EnumService from "services/enumService";

@Router.registerRoute({ route: ":documentInventoryId", name: Router.Self })
export default class DocumentsInventoryDetailViewModel extends DetailViewModel<AccountingDocumentsInventoryDetailResponse> {
  readonly modelName = "documentsInventory";

  constructor(
    protected itemId: string,
    private repository: AccountingDocumentsInventoryRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected enumService: EnumService
  ) {
    super(itemId);
  }

  @watchBusy
  protected loadDetail() {
    return this.repository.get(this.itemId);
  }

  @computed
  get statusTextClass(): string {
    const textMutedClass = "text-muted";
    if (
      this.item?.status === AccountingDocumentsInventoryStatus.Closed &&
      this.item?.checkResult === AccountingDocumentsInventoryCheckResult.Ok
    ) {
      return textMutedClass;
    }

    switch (this.item?.status) {
      case AccountingDocumentsInventoryStatus.ToBeChecked:
        return "text-dark-blue";
      case AccountingDocumentsInventoryStatus.Checked:
        return "text-success";
      case AccountingDocumentsInventoryStatus.Created:
        return "text-warning";
      case AccountingDocumentsInventoryStatus.CheckFailed:
        return "text-danger";
      case AccountingDocumentsInventoryStatus.Closed:
        return textMutedClass;
      default:
        return textMutedClass;
    }
  }

  @computed
  get checkResultTextClass(): string {
    const textMutedClass = "text-muted";
    if (
      this.item?.status === AccountingDocumentsInventoryStatus.Closed &&
      this.item?.checkResult === AccountingDocumentsInventoryCheckResult.Ok
    ) {
      return textMutedClass;
    }

    switch (this.item?.checkResult) {
      case AccountingDocumentsInventoryCheckResult.DifferencesFound:
        return "text-danger";
      case AccountingDocumentsInventoryCheckResult.Ok:
        return "text-success";
      default:
        return textMutedClass;
    }
  }

  @computed
  get inventoryAccounts() {
    return this.enumService.values("inventory_accounts");
  }

  @computed
  get accountFilesWithAccountInfo() {
    if (!this.item?.accountFiles) {
      return [];
    }

    return this.item.accountFiles.map(file => {
      const account = this.inventoryAccounts.find(acc => acc.id === file.inventoryAccountId);
      return {
        ...file,
        accountLabel: account ? `${account.syntheticAccount}-${account.analyticAccount}` : file.inventoryAccountId,
        accountName: account?.name,
      };
    });
  }

  @action.bound
  async onDownloadFile(inventoryAccountId: string) {
    try {
      const response = await this.repository.getAccountFile(this.itemId, inventoryAccountId);
      const accountFile = this.item?.accountFiles?.find(file => file.inventoryAccountId === inventoryAccountId);
      const defaultFileName = accountFile?.fileName ?? "file.xlsx";
      download(response.data, parseFileNameFromAxiosResponse(response, defaultFileName));
    } catch (error) {
      this.notificationService.add({
        title: this.translate("downloadFileError.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @action.bound
  @watchBusy
  async onDownloadCheckResult() {
    try {
      const response = await this.repository.checkResultFile(this.itemId);
      const defaultFileName = `Vysledek_kontroly_inventury_${this.item?.accountingPeriodMonthRange?.from}_${this.item?.accountingPeriodMonthRange?.to}.xlsx`;
      download(response.data, parseFileNameFromAxiosResponse(response, defaultFileName));
    } catch (error) {
      this.notificationService.add({
        title: this.translate("downloadCheckResultError.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @action.bound
  @watchBusy
  async onCloseInventory() {
    try {
      await this.repository.closures(this.itemId);
      this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
      this.requestClose();
    } catch (error) {
      this.notificationService.add({
        title: this.translate("closure.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @action.bound
  onClose() {
    this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.documentsInventory.detail.${code}`, size);
  }

  translateStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.documentsInventory.statusTypes.${code}`, size);
  }

  translateCheckResult(code: string, size?: number) {
    return this.localizationService.translateModule(
      Modules.finance,
      `inventory.documentsInventory.checkResultTypes.${code}`,
      size
    );
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new DocumentsInventoryDetailViewModel(
        itemId,
        container.get(AccountingDocumentsInventoryRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(EnumService)
      );
    };
  }
}
